import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { Household, Member } from '../../lib/supabase'
import type { ThemePref } from '../../lib/theme'
import type { Prices } from '../../hooks/usePrices'
import { usePrefs } from '../../hooks/usePrefs'
import { SheetPanel } from '../SheetPanel'
import { Segmented } from '../ui/Segmented'
import { Switch } from '../ui/Switch'
import { Section } from './Section'
import { HouseholdSection } from './HouseholdSection'
import { SubscriptionSection } from './SubscriptionSection'
import { WatchSection, type WatchState } from './WatchSection'
import { JoinSection } from './JoinSection'
import { AboutFooter } from './AboutFooter'

export type { WatchState }

export interface SettingsProps {
  open: boolean
  email: string
  household: Household
  self: Member | null
  members: Member[]
  watch: WatchState
  prices: Prices
  theme: { pref: ThemePref; setPref: (p: ThemePref) => void }
  billingLoading: boolean
  onRename: (name: string) => void
  onRemoveMember: (memberId: string) => Promise<string | null>
  onResetInviteCode: () => Promise<string | null>
  onSwitchHousehold: (code: string) => Promise<string | null>
  onUpgrade: () => void
  onManageSubscription: () => void
  onCategories: () => void
  /** Opens Item history (ItemHistorySheet): forget past items for good. */
  onItemHistory: () => void
  /** Settings › Your data (useDataExport): saves the household's file; an error to show, or null. */
  onExport: () => Promise<string | null>
  onSignOut: () => void
  /** Opened from the watch's QR code (?pair): point at Add watch, never mint a code. */
  autoAddWatch?: boolean
  onClose: () => void
}

const APPEARANCE: { value: ThemePref; label: string }[] = [
  { value: 'system', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

// Settings (feature spec 06): a bottom sheet on the phone, a 520 px dialog on
// desktop. The body mounts only while open, so confirmations and fields reset
// on close.
export function SettingsSheet(props: SettingsProps) {
  return <AnimatePresence>{props.open && <Body {...props} />}</AnimatePresence>
}

function Body(p: SettingsProps) {
  const { prefs, toggle } = usePrefs()
  return (
    <SheetPanel onClose={p.onClose} maxHeight="92dvh" layout="scroll" ariaLabel="Settings">
      <div className="ml-settings">
        <header className="ml-settings__head">
          <h2 className="ml-display ml-settings__heading">Settings</h2>
          <button type="button" className="ml-btn ml-btn--secondary" onClick={p.onClose}>Done</button>
        </header>

        <HouseholdSection household={p.household} members={p.members} self={p.self}
          onRename={p.onRename} onRemoveMember={p.onRemoveMember} onResetInviteCode={p.onResetInviteCode}
          onLeft={p.onClose} />

        <SubscriptionSection household={p.household} memberId={p.self?.id ?? null} prices={p.prices}
          loading={p.billingLoading} onUpgrade={p.onUpgrade} onManage={p.onManageSubscription} />

        <Section title="Appearance">
          <Segmented ariaLabel="Appearance" options={APPEARANCE} value={p.theme.pref} onChange={p.theme.setPref} />
        </Section>

        <Section title="Shopping and notifications">
          <Switch label="Running low suggestions" description="Suggest things you buy often when they are due"
            checked={prefs.restock} onChange={() => toggle('restock')} />
          <Switch label="Trip summary" description="Your time, pace and splits when you finish a trip"
            checked={prefs.tripSummary} onChange={() => toggle('tripSummary')} />
          <Switch label="Vibration" description="A short buzz when you tick things off"
            checked={prefs.haptics} onChange={() => toggle('haptics')} />
          <Switch label="When a cooking timer ends" description="Sound and vibration in cooking mode"
            checked={prefs.notifyTimer} onChange={() => toggle('notifyTimer')} />
          <Switch label="Get started" description="The setup steps at the top of the list"
            checked={prefs.getStarted} onChange={() => toggle('getStarted')} />
        </Section>

        <Section title="Categories">
          <button type="button" className="ml-text-btn" aria-label="Edit categories" onClick={p.onCategories}>
            Rename, add or remove categories
          </button>
        </Section>

        <Section title="Item history">
          <button type="button" className="ml-text-btn" onClick={p.onItemHistory}>
            Forget items you no longer buy
          </button>
        </Section>

        <DataSection onExport={p.onExport} />

        <WatchSection watch={p.watch} householdName={p.household.name} autoAddWatch={p.autoAddWatch} />

        <JoinSection householdName={p.household.name} onSwitchHousehold={p.onSwitchHousehold} onJoined={p.onClose} />

        <AboutFooter onSignOut={p.onSignOut} />
      </div>
    </SheetPanel>
  )
}

// Settings › Your data: the terms and privacy policy promise an export at any
// time, on any plan. One file, everything the household has (lib/dataExport.ts).
function DataSection({ onExport }: { onExport: () => Promise<string | null> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function run() {
    setBusy(true)
    setError(null)
    setError(await onExport())
    setBusy(false)
  }
  return (
    <Section title="Your data">
      <button type="button" className="ml-text-btn" onClick={() => void run()} disabled={busy}>
        {busy ? 'Gathering your data…' : 'Download your data'}
      </button>
      <p className="ml-settings__sub">Your list, its history, recipes, plans and trips, as one file.</p>
      {error && <p className="ml-settings__error" role="alert">{error}</p>}
    </Section>
  )
}
